import { useApp } from '@/contexts';

import { Alert, Box, CircularProgress, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router';

import { PageLoader } from '@/components/common';
import { PageContainer, PageHeading } from '@/components/layout';
import { logger } from '@/utils/logger';

import { useGetWizardQuery } from '../api/wizardsApi';
import { WizardFormStepper } from '../components/WizardFormStepper';
import { useWizardConfig, useWizardLoading, useWizardState, useWizardSubmit } from '../hooks';
import type { WizardConfig, WizardStepData } from '../types/wizard';

/**
 * Wizard Form Page - Multi-step wizard configuration
 * Based on dataProtect wizardFormWrapper.js
 *
 * Supports:
 * - Creating new wizards from wizard type (via /wiz/start/:wizardID with typeId)
 * - Resuming from saved drafts (via /wiz/:transId/form with piGuid)
 * - Saving partial progress ("Save and Exit")
 */
export const WizardFormPage = () => {
  const { appId } = useApp();
  const { wizardID, transId } = useParams<{
    wizardID?: string;
    transId?: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Load existing wizard if editing/resuming
  const { data: existingWizard, isLoading: isLoadingWizard } = useGetWizardQuery(
    { appId: appId!, wizardId: transId! },
    { skip: !transId }
  );

  // Get existing config if resuming draft
  const existingConfig = existingWizard?.versions[0]?.config;

  // Use wizard config hook to load/build configuration
  const {
    config: wizardConfig,
    steps,
    isLoading: isLoadingConfig,
  } = useWizardConfig(wizardID, appId!, existingConfig);

  // Get source/destination from navigation state OR from wizard config
  // Extract with explicit type checking to avoid TypeScript narrowing issues
  let source: WizardConfig['source'] | null = null;
  let destination: WizardConfig['destination'] | null = null;

  // Try to get from navigation state
  if (location.state && typeof location.state === 'object') {
    const state = location.state as {
      source?: WizardConfig['source'];
      destination?: WizardConfig['destination'];
    };
    source = state.source ?? null;
    destination = state.destination ?? null;
  }

  // Fallback to wizard config
  if (!source && wizardConfig) {
    source = (wizardConfig as WizardConfig).source;
  }
  if (!destination && wizardConfig) {
    destination = (wizardConfig as WizardConfig).destination;
  }

  // Initialize wizard state
  const initialData: WizardStepData | undefined =
    existingWizard?.status === 'draft'
      ? {
          name: existingWizard.name,
          currentStep: existingWizard.versions[0]?.config.currentStep,
          ...existingWizard.versions[0]?.config,
        }
      : undefined;

  const initialStep = initialData?.currentStep || 0;

  const {
    activeStep,
    wizardData,
    highestStep,
    showApiNames,
    updateData,
    nextStep,
    previousStep,
    goToStep,
    toggleApiNames,
  } = useWizardState({ initialData, initialStep });

  // Loading and error states
  const { isGenerating, loadingMessage, error, setGenerating, setError, reset } =
    useWizardLoading();

  // Submit handlers (pass wizardConfig which could be null, hook validates internally)
  const { submitDraft, submitComplete } = useWizardSubmit(
    appId!,
    (wizardConfig as WizardConfig | null) ?? null,
    wizardData
  );

  if (!source || !destination) {
    return (
      <PageContainer>
        <PageHeading
          title="Configure Wizard"
          subtitle="Complete the wizard configuration steps to create your integration"
        />
        <Alert severity="warning">
          Invalid wizard configuration. Please start from the wizard list.
        </Alert>
      </PageContainer>
    );
  }

  // Create fallback config with validated source/destination
  const fallbackConfig: WizardConfig = {
    title: '',
    typeGuid: '',
    typeId: 0,
    source,
    destination,
  };

  const finalConfig: WizardConfig = (wizardConfig as WizardConfig | null) ?? fallbackConfig;

  const handleComplete = async () => {
    if (!wizardData.name) return;

    setGenerating('Your Integration is Being Generated...');
    try {
      const wizard = await submitComplete();
      reset();
      navigate(`/a/${appId}/wiz/${wizard.baseId}`);
    } catch (err: any) {
      setError(err?.message || 'Failed to create wizard. Please try again.');
    }
  };

  const handleCancel = () => {
    navigate(`/a/${appId}/wiz`);
  };

  const handleSaveAndExit = async () => {
    if (!wizardData.name) return;

    try {
      await submitDraft(activeStep, transId);
      navigate(`/a/${appId}/wiz`, {
        state: { message: 'Wizard draft saved successfully. You can continue later.' },
      });
    } catch (err) {
      logger.error('Failed to save wizard draft:', err);
    }
  };

  const handleExitWithoutSaving = () => {
    if (window.confirm('Exit without saving? All progress will be lost.')) {
      navigate(`/a/${appId}/wiz`);
    }
  };

  // Loading state
  if (isLoadingWizard || isLoadingConfig) {
    return (
      <PageContainer>
        <PageHeading title="Configure Wizard" />
        <PageLoader message="Loading wizard configuration..." />
      </PageContainer>
    );
  }

  // Generation loading overlay
  if (isGenerating) {
    return (
      <Dialog open maxWidth="sm" fullWidth>
        <DialogTitle>Creating Integration</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Box sx={{ textAlign: 'center' }}>{loadingMessage}</Box>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // Error state
  if (error) {
    return (
      <PageContainer>
        <PageHeading title="Configure Wizard" />
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <button onClick={() => reset()}>Try Again</button>
          <button onClick={handleCancel}>Cancel</button>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeading
        title={transId ? 'Continue Wizard' : 'Configure Wizard'}
        subtitle={source && destination ? `${source.label} → ${destination.label}` : ''}
      />
      <WizardFormStepper
        config={finalConfig}
        steps={steps || []}
        activeStep={activeStep}
        highestStep={highestStep}
        showApiNames={showApiNames}
        wizardData={wizardData}
        onUpdateData={updateData}
        onNext={nextStep}
        onPrevious={previousStep}
        onGoToStep={goToStep}
        onToggleApiNames={toggleApiNames}
        onSaveAndExit={handleSaveAndExit}
        onExitWithoutSaving={handleExitWithoutSaving}
        onComplete={handleComplete}
        onCancel={handleCancel}
      />
    </PageContainer>
  );
};
